/**
 * Time handling for the recording timeline.
 *
 * Invariant: every timestamp in the system is an integer number of milliseconds
 * measured from `recordings.t0_utc`, which is the first frame of the media file
 * we serve for playback. Nothing is stored in seconds, nothing is stored as a
 * float, and nothing is measured from any other origin.
 *
 * This is what makes click-to-seek exact. See CLAUDE.md invariants 1 and 2.
 */

/** Milliseconds from the start of the recording. */
export type Ms = number;

/** Convert a Recall/ASR relative timestamp (float seconds) to Ms. */
export function secondsToMs(seconds: number): Ms {
  return Math.round(seconds * 1000);
}

/** Convert Ms to the float seconds a media element expects. */
export function msToSeconds(ms: Ms): number {
  return ms / 1000;
}

/**
 * Convert an absolute wall-clock time to an offset from the recording origin.
 * Used for platform signals (participant join/leave) that arrive as UTC.
 */
export function absoluteToMs(at: Date, t0: Date): Ms {
  return at.getTime() - t0.getTime();
}

/** `1:23` under an hour, `1:02:03` at or over it. Never pads the leading unit. */
export function formatTimecode(ms: Ms): string {
  const clamped = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(clamped / 3600);
  const minutes = Math.floor((clamped % 3600) / 60);
  const seconds = clamped % 60;

  const pad = (n: number) => n.toString().padStart(2, "0");

  return hours > 0
    ? `${hours}:${pad(minutes)}:${pad(seconds)}`
    : `${minutes}:${pad(seconds)}`;
}

/** Parse `1:23` or `1:02:03` back to Ms. Returns null on anything else. */
export function parseTimecode(value: string): Ms | null {
  const parts = value.trim().split(":");
  if (parts.length < 2 || parts.length > 3) return null;

  const numbers = parts.map((p) => Number(p));
  if (numbers.some((n) => !Number.isInteger(n) || n < 0)) return null;

  const [a, b, c] = numbers as [number, number, number?];
  const totalSeconds = c === undefined ? a * 60 + b : a * 3600 + b * 60 + c;

  return totalSeconds * 1000;
}

/** Human duration for lists: `24 s`, `45 min`, `1 h 12 min`. */
export function formatDuration(ms: Ms): string {
  // Under a minute says so in seconds — "0 min" reads as broken, not short.
  if (ms < 59_500) return `${Math.max(1, Math.round(ms / 1000))} s`;

  const totalMinutes = Math.round(ms / 60_000);
  if (totalMinutes < 60) return `${totalMinutes} min`;

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes === 0 ? `${hours} h` : `${hours} h ${minutes} min`;
}

/**
 * Index of the last item whose `start_ms` is at or before `ms`.
 * Returns -1 when `ms` precedes the first item.
 *
 * Called on every `timeupdate` — several times a second — so it must stay a
 * binary search over a plain sorted array. Do not put this data in React state.
 */
export function findActiveIndex(startTimes: readonly Ms[], ms: Ms): number {
  let low = 0;
  let high = startTimes.length - 1;
  let result = -1;

  while (low <= high) {
    const mid = (low + high) >>> 1;
    const start = startTimes[mid];
    if (start === undefined) break;

    if (start <= ms) {
      result = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  return result;
}

/**
 * Force chapters into a clean partition of the recording.
 *
 * A model asked for chapters "with no gaps and no overlap" does not reliably
 * produce them. One real recording came back with 84 overlapping pairs — one
 * chapter ending at 8:47 while the next began at 8:16 — because each boundary
 * is chosen independently and nothing checked them against each other.
 *
 * Overlap breaks the product in two visible ways. Highlighting "the chapter
 * containing now" picks whichever overlapping one is listed first, so the list
 * highlights a chapter minutes away from where playback is; and the player's
 * own chapter track gets overlapping cues, so its title bar names the wrong
 * chapter too.
 *
 * Each chapter therefore ends exactly where the next begins. That is the only
 * arrangement in which "which chapter is this moment in" has one answer.
 *
 * Applied when chapters are written *and* when they are read, deliberately:
 * writing fixes them going forward, reading fixes every recording chaptered
 * before this existed without asking anybody to regenerate.
 */
export function normaliseChapters<T extends { startMs: Ms; endMs: Ms }>(
  chapters: readonly T[],
  durationMs?: Ms | null,
): T[] {
  if (chapters.length === 0) return [];

  const sorted = [...chapters].sort((a, b) => a.startMs - b.startMs);

  // Two chapters claiming the same start have no order between them and would
  // give a zero-length chapter below. Keep the first.
  const unique = sorted.filter(
    (chapter, index) => index === 0 || chapter.startMs > sorted[index - 1]!.startMs,
  );

  return unique.map((chapter, index) => {
    const next = unique[index + 1];

    // The last chapter runs to the end of the recording where we know it — a
    // chapter that stops short leaves the final minutes belonging to nothing,
    // and nothing is what the list would then highlight.
    const end = next
      ? next.startMs
      : Math.max(chapter.endMs, durationMs ?? chapter.endMs);

    return {
      ...chapter,
      // The first chapter starts at zero. The model often begins at the first
      // substantive line, leaving the opening minutes uncovered.
      startMs: index === 0 ? 0 : chapter.startMs,
      endMs: Math.max(end, chapter.startMs),
    };
  });
}

export type SubtitleCue = { startMs: Ms; endMs: Ms; text: string };

/**
 * Which way a line of mixed text should actually read.
 *
 * `dir="auto"` and `unicode-bidi: plaintext` both decide from the **first
 * strong character**, which is the wrong rule for the sentences this product
 * handles. "Donc حتى اليوم الله يبارك كاين سبعة des formations" is an Arabic
 * sentence that happens to open with a French discourse marker, and first-strong
 * reads that "Donc" and lays the whole line out left-to-right — so every Arabic
 * run inside it is positioned as an LTR block and the sentence comes apart.
 *
 * Counting is the rule that matches what people mean: the script most of the
 * line is written in is the script the line is in, wherever the first word came
 * from. A genuinely French line inside an Arabic meeting still resolves to
 * left-to-right, because there the count says so too.
 */
export function dominantDirection(text: string): "rtl" | "ltr" {
  // Arabic, Hebrew, Syriac, Thaana, and the Arabic presentation forms.
  const RTL = /[\u0590-\u05FF\u0600-\u06FF\u0700-\u074F\u0780-\u07BF\u08A0-\u08FF\uFB1D-\uFDFF\uFE70-\uFEFF]/;
  // Latin, including accented forms — "à l'international" is Latin.
  const LTR = /[A-Za-z\u00C0-\u024F]/;

  /**
   * Counted in words, not characters.
   *
   * Characters are the obvious measure and the wrong one. In
   * "Donc حتى اليوم الله يبارك الحمد لله كاين حوالي سبعة des formations بينهم
   * des formations à l'international" there are 43 Arabic letters against 45
   * Latin ones — so by character count this Arabic sentence is French, by two
   * letters. Arabic words are short and French words are long, which says
   * nothing about what language somebody is speaking.
   *
   * Ten Arabic words against seven Latin ones is the number that matches how it
   * reads.
   */
  let rtl = 0;
  let ltr = 0;
  let first: "rtl" | "ltr" | null = null;

  for (const word of text.split(/\s+/)) {
    const isRtl = RTL.test(word);
    const isLtr = !isRtl && LTR.test(word);

    if (isRtl) rtl++;
    else if (isLtr) ltr++;
    else continue;

    first ??= isRtl ? "rtl" : "ltr";
  }

  if (rtl !== ltr) return rtl > ltr ? "rtl" : "ltr";

  // A genuine tie — fall back to what the line opens with, which is the
  // standard heuristic and as good an answer as exists.
  return first ?? "ltr";
}

/**
 * Invisible marks that set a line's base direction.
 *
 * The player renders cue text itself, so there is no element to put `dir` on —
 * but a leading RIGHT-TO-LEFT MARK is a strong character with no glyph, and
 * first-strong then resolves to the direction we actually want. It is the only
 * way to steer a base direction through plain text.
 */
const DIRECTION_MARK = { rtl: "\u200F", ltr: "\u200E" } as const;

/** Prefix a line so it reads in the direction most of it is written in. */
export function withBaseDirection(text: string): string {
  return DIRECTION_MARK[dominantDirection(text)] + text;
}

/**
 * A timed word, described structurally rather than imported.
 *
 * `Word` lives in `schemas.ts`, which imports this module for `Ms` — taking it
 * back would make the two circular. Only these three fields are needed, and a
 * `Word` satisfies them.
 */
type TimedWord = { text: string; startMs: Ms; endMs: Ms };

/**
 * Turn utterances into subtitle-sized cues.
 *
 * Utterances are the wrong size to read off a screen. They are stored finely
 * for seeking and citation — one per sentence or so — but `splitIntoUtterances`
 * allows up to thirty seconds and ninety words, and a thirty-second subtitle is
 * a paragraph nailed over the video.
 *
 * The timings come from the words themselves. Every utterance carries per-word
 * start and end times from the provider, so each cue takes its start from its
 * first word and its end from its last — exact, never interpolated. That is the
 * same principle as invariant 3, and it is why this can be done at display time
 * rather than needing a separate generated file: nothing is guessed, so nothing
 * can drift out of step with the audio.
 *
 * Cues never span two utterances, so a cue never mixes two speakers.
 */
export function toSubtitleCues(
  utterances: ReadonlyArray<{
    startMs: Ms;
    endMs: Ms;
    text: string;
    words: ReadonlyArray<TimedWord>;
  }>,
  options: { maxWords?: number; maxMs?: Ms; minMs?: Ms } = {},
): SubtitleCue[] {
  /**
   * Roughly two lines at a readable size, and long enough to finish reading.
   *
   * Longer than strict subtitling convention on purpose. Broadcast practice
   * assumes a viewer who cannot rewind; here a fuller clause reads better than
   * a sentence chopped into fragments that each vanish before the thought
   * completes — and anyone can scrub back.
   *
   * Both ceilings are needed, because either alone lets the other run away:
   * eleven words spoken slowly is still too long on screen, and five seconds of
   * fast speech is too many words.
   */
  const maxWords = options.maxWords ?? 11;
  const maxMs = options.maxMs ?? 5_000;
  const minMs = options.minMs ?? 900;

  const cues: SubtitleCue[] = [];

  for (const utterance of utterances) {
    // No word timings — an older recording, or a provider that gave none. One
    // cue for the whole line is worse than nothing only if it is silently
    // wrong, and it is not: its bounds are the utterance's own.
    if (utterance.words.length === 0) {
      if (utterance.text.trim()) {
        cues.push({
          startMs: utterance.startMs,
          endMs: Math.max(utterance.endMs, utterance.startMs + minMs),
          text: withBaseDirection(utterance.text.trim()),
        });
      }
      continue;
    }

    let group: TimedWord[] = [];

    const flush = () => {
      if (group.length === 0) return;
      const first = group[0]!;
      const last = group[group.length - 1]!;
      const text = group
        .map((w) => w.text)
        .join(" ")
        // Punctuation belongs to the word before it, as in the transcript.
        .replace(/\s+([,.!?;:،؟])/g, "$1")
        .trim();

      if (text) {
        cues.push({
          startMs: first.startMs,
          endMs: Math.max(last.endMs, first.startMs + 1),
          text: withBaseDirection(text),
        });
      }
      group = [];
    };

    for (const word of utterance.words) {
      group.push(word);

      const start = group[0]!.startMs;
      const tooMany = group.length >= maxWords;
      const tooLong = word.endMs - start >= maxMs;

      if (tooMany || tooLong) flush();
    }

    flush();
  }

  return holdAndClamp(cues, minMs);
}

/**
 * Hold a short cue on screen a little longer, but never into the next one.
 *
 * A three-word cue can be under half a second, which reads as a flicker. The
 * clamp against the following cue is what keeps two from being visible at
 * once — overlapping cues stack and cover the video.
 */
function holdAndClamp(cues: SubtitleCue[], minMs: Ms): SubtitleCue[] {
  return cues.map((cue, index) => {
    const next = cues[index + 1];
    const wanted = cue.startMs + minMs;
    const ceiling = next ? next.startMs : Number.POSITIVE_INFINITY;

    return { ...cue, endMs: Math.min(Math.max(cue.endMs, wanted), ceiling) };
  });
}

/**
 * Subtitle cues in a translated language.
 *
 * A translation has no word timings — the model returns one string per
 * utterance, and translated words do not map one-to-one onto spoken ones (a
 * German verb may render the end of an English sentence). So exactness is only
 * possible at utterance boundaries, and that is what this promises: each
 * utterance's translated text appears exactly while that utterance is being
 * spoken. *Within* an utterance, text too long for one cue is split into
 * balanced chunks and timed proportionally by character count — interpolation,
 * disclosed as such, and confined to spans a sentence long.
 *
 * An utterance with no translation falls back to its original text: a caption
 * in the source language beats a silent gap that reads as a subtitle bug.
 */
export function toTranslatedSubtitleCues(
  utterances: ReadonlyArray<{
    id: string;
    startMs: Ms;
    endMs: Ms;
    text: string;
    words: ReadonlyArray<TimedWord>;
  }>,
  translationById: ReadonlyMap<string, string>,
  options: { maxChars?: number; spaceless?: boolean; minMs?: Ms } = {},
): SubtitleCue[] {
  // Two subtitle lines of ~42 characters; half that for CJK, whose glyphs are
  // roughly double-width and carry more meaning per character.
  const maxChars = options.maxChars ?? (options.spaceless ? 36 : 84);
  const minMs = options.minMs ?? 900;

  const cues: SubtitleCue[] = [];

  for (const utterance of utterances) {
    const translated = translationById.get(utterance.id)?.trim();

    if (!translated) {
      cues.push(...toSubtitleCues([utterance], { minMs }));
      continue;
    }

    const chunks = chunkText(translated, maxChars, options.spaceless ?? false);
    const totalChars = chunks.reduce((n, c) => n + c.length, 0) || 1;
    const span = Math.max(utterance.endMs - utterance.startMs, 1);

    let atChars = 0;
    for (const chunk of chunks) {
      const startMs = Math.round(utterance.startMs + (span * atChars) / totalChars);
      atChars += chunk.length;
      const endMs = Math.round(utterance.startMs + (span * atChars) / totalChars);

      cues.push({
        startMs,
        endMs: Math.max(endMs, startMs + 1),
        text: withBaseDirection(chunk),
      });
    }
  }

  return holdAndClamp(cues, minMs);
}

/**
 * Split text into cue-sized pieces of roughly equal length.
 *
 * Balanced on purpose: a naive greedy fill turns "just over one cue" into a
 * full cue plus a two-word orphan flashing past. Dividing the text into
 * `ceil(len / max)` even targets keeps every piece readable.
 */
function chunkText(text: string, maxChars: number, spaceless: boolean): string[] {
  if (text.length <= maxChars) return [text];

  const pieces = Math.ceil(text.length / maxChars);
  const target = Math.ceil(text.length / pieces);

  if (spaceless) {
    // No word boundaries to respect — but break after sentence punctuation
    // when it falls near the target, so a cue does not open mid-clause.
    const out: string[] = [];
    let rest = text;
    while (rest.length > 0) {
      if (rest.length <= target + 4) {
        out.push(rest);
        break;
      }
      const window = rest.slice(0, target + 4);
      const punct = Math.max(
        ...["。", "，", "！", "？", "；", "、"].map((p) => window.lastIndexOf(p)),
      );
      const cut = punct >= Math.floor(target / 2) ? punct + 1 : target;
      out.push(rest.slice(0, cut));
      rest = rest.slice(cut);
    }
    return out.map((s) => s.trim()).filter(Boolean);
  }

  const words = text.split(/\s+/);
  const out: string[] = [];
  let current = "";
  let remainingPieces = pieces;
  let remainingLen = text.length;

  for (const word of words) {
    // The target is recomputed against what is left, so rounding never
    // strands a final two-word orphan: the last piece absorbs the remainder
    // instead of overflowing into a fragment that flashes past.
    const share = remainingLen / remainingPieces;
    const candidate = current ? `${current} ${word}` : word;

    if (current && candidate.length > share && remainingPieces > 1) {
      out.push(current);
      remainingLen -= current.length + 1;
      remainingPieces -= 1;
      current = word;
    } else {
      current = candidate;
    }
  }
  if (current) out.push(current);
  return out;
}

/**
 * Serialise cues as SubRip, the format ffmpeg's subtitle burn-in reads.
 *
 * The same cues the player renders, so a burned export shows exactly what
 * preview showed — one implementation, two consumers. The direction marks
 * `withBaseDirection` prefixed survive here on purpose: libass runs its own
 * bidi resolution and the leading RLM steers it the same way it steers the
 * player.
 */
export function cuesToSrt(cues: ReadonlyArray<SubtitleCue>): string {
  const stamp = (ms: Ms) => {
    const h = Math.floor(ms / 3_600_000);
    const m = Math.floor((ms % 3_600_000) / 60_000);
    const s = Math.floor((ms % 60_000) / 1000);
    const frac = Math.floor(ms % 1000);
    const pad = (n: number, w = 2) => String(n).padStart(w, "0");
    return `${pad(h)}:${pad(m)}:${pad(s)},${pad(frac, 3)}`;
  };

  return cues
    .map((cue, i) => {
      // A newline inside cue text would terminate the SRT block early and
      // orphan the rest — model-emitted translations can contain them.
      const text = cue.text.replace(/\s*\n\s*/g, " ");
      // A cue of zero length is legal SRT but some renderers drop it.
      return `${i + 1}\n${stamp(cue.startMs)} --> ${stamp(Math.max(cue.endMs, cue.startMs + 1))}\n${text}\n`;
    })
    .join("\n");
}

/** Total overlap in ms between two half-open intervals. Zero when disjoint. */
export function overlapMs(
  a: { startMs: Ms; endMs: Ms },
  b: { startMs: Ms; endMs: Ms },
): Ms {
  return Math.max(0, Math.min(a.endMs, b.endMs) - Math.max(a.startMs, b.startMs));
}
